import React, { useState, useEffect, useCallback } from "react";
import { cn } from "../../../lib/utils";
import {
  ShieldCheck,
  ShieldX,
  Library,
  RefreshCw,
  Save,
  Copy,
  Trash2,
  Eye,
  Edit,
  Pause,
  Play,
  ToggleLeft,
  ToggleRight,
  CheckCircle2,
  XCircle,
  ChevronUp,
  ChevronDown,
  Search,
  Brain,
  Plus,
} from "lucide-react";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import GatewayChatPanel from "../components/GatewayChatPanel";
import GatewayCedarEditor from "../components/GatewayCedarEditor";
import GatewayStatCard from "../components/GatewayStatCard";
import { GatewayPoliciesApi } from "../Api/gatewayApi";
import { effectColor, copyToClipboard, formatTimestamp } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import type {
  Policy,
  PolicyStats,
  PolicyChatResponse,
  ChatMessage,
} from "../interfaces/gatewayInterfaces";

type Tab = "workspace" | "library";
type SortField = "createdAt" | "name";
type SortDir = "asc" | "desc";

interface LibraryFilters {
  search: string;
  effect: string;
  enabled: string;
  source: string;
}


function EffectBadge({ effect }: { effect: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border",
        effectColor(effect)
      )}
    >
      {effect}
    </span>
  );
}

function EnabledToggle({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-1 text-xs transition-colors"
      title={enabled ? "Disable policy" : "Enable policy"}
    >
      {enabled ? (
        <ToggleRight className="w-5 h-5 text-emerald-400" />
      ) : (
        <ToggleLeft className="w-5 h-5 text-muted-foreground/70" />
      )}
      <span className={enabled ? "text-emerald-400" : "text-muted-foreground"}>
        {enabled ? "Enabled" : "Disabled"}
      </span>
    </button>
  );
}


export default function GatewayPoliciesPage() {
  const { toast } = useToast();

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [statsRaw, setStatsRaw] = useState<PolicyStats>({ total: 0, active: 0, permit: 0, forbid: 0 });
  const [loading, setLoading] = useState(true);

  const stats = {
    total: policies.length,
    active: policies.filter((p) => p.enabled).length,
    permit: policies.filter((p) => p.effect === "PERMIT").length,
    forbid: policies.filter((p) => p.effect === "FORBID").length,
    llmAvailable: statsRaw.llmAvailable,
  };

  const [tab, setTab] = useState<Tab>("workspace");

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [lastChatResp, setLastChatResp] = useState<PolicyChatResponse | null>(null);
  const [policyCode, setPolicyCode] = useState("");
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState<LibraryFilters>({
    search: "",
    effect: "",
    enabled: "",
    source: "",
  });
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  const [viewPolicy, setViewPolicy] = useState<Policy | null>(null);
  const [deletePolicy, setDeletePolicy] = useState<Policy | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [policiesData, statsData] = await Promise.all([
        GatewayPoliciesApi.list(),
        GatewayPoliciesApi.stats(),
      ]);
      setPolicies(policiesData || []);
      setStatsRaw(statsData || { total: 0, active: 0, permit: 0, forbid: 0 });
    } catch (err: any) {
      toast({ title: "Failed to load policies", description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChatSend = async (prompt: string) => {
    const userMsg: ChatMessage = { role: "user", content: prompt };
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setChatLoading(true);
    try {
      const requestData =
        newMessages.length > 1
          ? { messages: newMessages }
          : { prompt };
      const resp = await GatewayPoliciesApi.chat(requestData);

      if (resp?.success === false) {
        toast({ title: "AI error", description: resp.error || "Failed to generate", variant: "destructive" });
        setChatMessages(chatMessages);
        return;
      }

      if (!resp?.conversationComplete && resp?.followUpQuestion) {
        const asstMsg: ChatMessage = { role: "assistant", content: resp.followUpQuestion };
        setChatMessages([...newMessages, asstMsg]);
        return;
      }

      if (resp?.policyText) {
        const asstMsg: ChatMessage = { role: "assistant", content: resp.policyText };
        setChatMessages([...newMessages, asstMsg]);
        setLastChatResp(resp);
        setPolicyCode(resp.policyText);
        return;
      }

      toast({ title: "No policy generated", variant: "destructive" });
      setChatMessages(chatMessages);
    } catch (err: any) {
      toast({ title: "Chat error", description: err?.message, variant: "destructive" });
      setChatMessages(chatMessages);
    } finally {
      setChatLoading(false);
    }
  };

  const handleClearChat = () => {
    setChatMessages([]);
    setLastChatResp(null);
    setPolicyCode("");
  };

  const handleSavePolicy = async () => {
    const text = policyCode.trim();
    if (!text) {
      toast({ title: "Policy text is empty", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const body = {
        policyName: lastChatResp?.suggestedName || `workspace-policy-${Date.now()}`,
        policyText: text,
        description: lastChatResp?.description || "",
        effect: lastChatResp?.effect || "PERMIT",
        source: lastChatResp ? "LLM_GENERATED" : "MANUAL",
        enabled: true,
      };
      await GatewayPoliciesApi.create(body);
      toast({ title: "Policy saved successfully" });
      handleClearChat();
      await loadData();
    } catch (err: any) {
      toast({ title: "Save failed", description: err?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCode = async () => {
    if (!policyCode.trim()) {
      toast({ title: "Nothing to copy" });
      return;
    }
    await copyToClipboard(policyCode);
    toast({ title: "Copied to clipboard" });
  };

  const filteredPolicies = (() => {
    let result = policies.filter((p) => {
      if (filters.effect && p.effect !== filters.effect) return false;
      if (filters.enabled !== "") {
        const enabledBool = filters.enabled === "true";
        if (p.enabled !== enabledBool) return false;
      }
      if (filters.source && p.source !== filters.source) return false;
      return true;
    });
    if (filters.search && filters.search.trim()) {
      const q = filters.search.toLowerCase().trim();
      result = result.filter((p) => ((p as any).policyName || p.name || "").toLowerCase().startsWith(q));
    }
    return result;
  })()
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === "name") {
        cmp = a.name.localeCompare(b.name);
      } else {
        cmp =
          new Date(a.createdAt || 0).getTime() -
          new Date(b.createdAt || 0).getTime();
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

  const totalPages = Math.ceil(filteredPolicies.length / PAGE_SIZE);
  const pagedPolicies = filteredPolicies.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  );

  const uniqueSources = Array.from(new Set(policies.map((p) => p.source || "").filter(Boolean)));

  const handleToggleEnabled = async (p: Policy) => {
    const prev = [...policies];
    setPolicies((list) =>
      list.map((pol) => pol.id === p.id ? { ...pol, enabled: !pol.enabled } : pol)
    );
    try {
      await GatewayPoliciesApi.toggle(p.id);
      toast({
        title: p.enabled ? "Policy disabled" : "Policy enabled",
        description: p.policyName || p.name,
      });
    } catch (err: any) {
      setPolicies(prev);
      toast({ title: "Toggle failed", description: err?.message, variant: "destructive" });
    }
  };

  const handleEditPolicy = (p: Policy) => {
    setPolicyCode(p.policyText);
    setLastChatResp({
      policyText: p.policyText,
      suggestedName: p.name,
      effect: p.effect,
      source: p.source,
      description: p.description,
    });
    setTab("workspace");
  };

  const handleDeleteConfirm = async () => {
    if (!deletePolicy) return;
    setDeleteLoading(true);
    try {
      await GatewayPoliciesApi.remove(deletePolicy.id);
      toast({ title: `Policy "${deletePolicy.policyName || deletePolicy.name || deletePolicy.id}" deleted` });
      setDeletePolicy(null);
      await loadData();
    } catch (err: any) {
      toast({ title: "Delete failed", description: err?.message, variant: "destructive" });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReloadEngine = async () => {
    try {
      await GatewayPoliciesApi.reload();
      toast({ title: "Policy engine reloaded" });
    } catch (err: any) {
      toast({ title: "Reload failed", description: err?.message, variant: "destructive" });
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(0);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="w-3.5 h-3.5 inline ml-1" />
    ) : (
      <ChevronDown className="w-3.5 h-3.5 inline ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">Policy Management</h1>
          <p className="text-sm text-muted-foreground">Cedar-based authorization policies with AI-powered generation</p>
        </div>
        <div className="flex items-center gap-2">
          {stats?.llmAvailable !== false ? (
            <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-md">
              <Brain className="w-3.5 h-3.5" /> AI Active
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-md">
              No LLM
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleReloadEngine}
            className="text-xs border-border hover:border-border"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reload Engine
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        {(["workspace", "library"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
              tab === t
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground/80"
            )}
          >
            {t === "workspace" ? (
              <><Edit className="w-4 h-4" /> Workspace</>
            ) : (
              <>
                <Library className="w-4 h-4" /> Policy Library
                {policies.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-muted text-foreground/80 text-xs">
                    {policies.length}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </div>

      {tab === "workspace" ? (
        <WorkspaceTab
          chatMessages={chatMessages}
          chatLoading={chatLoading}
          lastChatResp={lastChatResp}
          policyCode={policyCode}
          saving={saving}
          onChatSend={handleChatSend}
          onClearChat={handleClearChat}
          onPolicyCodeChange={setPolicyCode}
          onSave={handleSavePolicy}
          onCopy={handleCopyCode}
        />
      ) : (
        <LibraryTab
          policies={pagedPolicies}
          stats={stats}
          filters={filters}
          sortBy={sortBy}
          sortDir={sortDir}
          page={page}
          totalPages={totalPages}
          totalFiltered={filteredPolicies.length}
          uniqueSources={uniqueSources}
          onFilterChange={(key, val) => {
            setFilters((f) => ({ ...f, [key]: val }));
            setPage(0);
          }}
          onSortToggle={toggleSort}
          SortIcon={SortIcon}
          onPageChange={setPage}
          onView={setViewPolicy}
          onEdit={handleEditPolicy}
          onDelete={setDeletePolicy}
          onToggle={handleToggleEnabled}
          onReload={handleReloadEngine}
        />
      )}

      <Dialog open={!!viewPolicy} onOpenChange={() => setViewPolicy(null)}>
        <DialogContent className="max-w-2xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewPolicy && <EffectBadge effect={viewPolicy.effect} />}
              {viewPolicy?.name}
            </DialogTitle>
          </DialogHeader>
          {viewPolicy && (
            <div className="space-y-4">
              {viewPolicy.description && (
                <p className="text-sm text-muted-foreground">{viewPolicy.description}</p>
              )}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-muted-foreground">Source</span>
                  <p className="text-foreground/80 mt-0.5">{viewPolicy.source || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <p className={cn("mt-0.5", viewPolicy.enabled ? "text-emerald-400" : "text-muted-foreground")}>
                    {viewPolicy.enabled ? "Enabled" : "Disabled"}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <p className="text-foreground/80 mt-0.5">{formatTimestamp(viewPolicy.createdAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated</span>
                  <p className="text-foreground/80 mt-0.5">{formatTimestamp(viewPolicy.updatedAt)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Policy Text</p>
                <GatewayCedarEditor
                  value={viewPolicy.policyText}
                  onChange={() => {}}
                  readOnly
                  className="min-h-[200px]"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="border-border"
              onClick={() => setViewPolicy(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletePolicy} onOpenChange={() => setDeletePolicy(null)}>
        <DialogContent className="max-w-md bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle>Delete Policy</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{" "}
            <span className="font-mono text-foreground font-medium bg-muted/50 px-1.5 py-0.5 rounded">{deletePolicy?.policyName || deletePolicy?.name || deletePolicy?.id}</span>? This action
            cannot be undone.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-border"
              onClick={() => setDeletePolicy(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-500 text-white"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              {deleteLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


interface WorkspaceTabProps {
  chatMessages: ChatMessage[];
  chatLoading: boolean;
  lastChatResp: PolicyChatResponse | null;
  policyCode: string;
  saving: boolean;
  onChatSend: (msg: string) => void;
  onClearChat: () => void;
  onPolicyCodeChange: (val: string) => void;
  onSave: () => void;
  onCopy: () => void;
}

function WorkspaceTab({
  chatMessages,
  chatLoading,
  lastChatResp,
  policyCode,
  saving,
  onChatSend,
  onClearChat,
  onPolicyCodeChange,
  onSave,
  onCopy,
}: WorkspaceTabProps) {
  return (
    <div className="grid grid-cols-2 gap-4 h-[620px]">
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-medium text-foreground/80">Policy Assistant</span>
          {chatMessages.length > 0 && (
            <button
              onClick={onClearChat}
              className="text-xs text-muted-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> New
            </button>
          )}
        </div>
        <GatewayChatPanel
          messages={chatMessages}
          onSend={onChatSend}
          loading={chatLoading}
          welcomeMessage="I can help you create Cedar authorization policies."
          suggestions={[
            "Block all tool calls after hours",
            "Allow read-only access to database tools",
            "Require human approval for destructive actions",
          ]}
          placeholder={
            chatMessages.length > 0
              ? "Refine this policy or ask a follow-up..."
              : "Describe your policy in natural language..."
          }
          className="flex-1 min-h-0"
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <span className="text-sm font-medium text-foreground/80">Cedar Policy Editor</span>
            <div className="flex items-center gap-2">
              <button
                onClick={onCopy}
                className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground/80 transition-colors"
                title="Copy code"
              >
                <Copy className="w-4 h-4" />
              </button>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary text-white text-xs"
                onClick={onSave}
                disabled={saving || !policyCode.trim()}
              >
                {saving ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                )}
                Save Policy
              </Button>
            </div>
          </div>
          {lastChatResp?.semanticWarning && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-400 text-xs">
              <ShieldX className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1">{lastChatResp.semanticWarning}</span>
            </div>
          )}
          <div className="flex-1 min-h-0 p-3">
            <GatewayCedarEditor
              value={policyCode}
              onChange={onPolicyCodeChange}
              className="h-full"
            />
          </div>
        </div>

        <PolicyContextPanel resp={lastChatResp} />
      </div>
    </div>
  );
}

function PolicyContextPanel({ resp }: { resp: PolicyChatResponse | null }) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-4 space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Policy Context</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</p>
          <p className="text-sm text-foreground/80 mt-0.5">{resp?.suggestedName || "-"}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Effect</p>
          <div className="mt-0.5">
            {resp?.effect ? <EffectBadge effect={resp.effect} /> : <span className="text-sm text-muted-foreground">-</span>}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Source</p>
          <p className="text-sm text-foreground/80 mt-0.5">
            {resp?.source === "LLM_GENERATED" ? (
              <span className="flex items-center gap-1 text-primary">
                <Brain className="w-3.5 h-3.5" /> AI Generated
              </span>
            ) : resp?.source ? resp.source : "-"}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</p>
          <p className="text-sm text-foreground/80 mt-0.5 truncate" title={resp?.description}>
            {resp?.description || "-"}
          </p>
        </div>
      </div>
      {resp?.explanation && (
        <p className="text-xs text-muted-foreground italic border-t border-border pt-2 leading-relaxed">
          {resp.explanation}
        </p>
      )}
    </div>
  );
}


interface LibraryTabProps {
  policies: Policy[];
  stats: PolicyStats;
  filters: LibraryFilters;
  sortBy: SortField;
  sortDir: SortDir;
  page: number;
  totalPages: number;
  totalFiltered: number;
  uniqueSources: string[];
  onFilterChange: (key: string, val: string) => void;
  onSortToggle: (field: SortField) => void;
  SortIcon: React.ComponentType<{ field: SortField }>;
  onPageChange: (p: number) => void;
  onView: (p: Policy) => void;
  onEdit: (p: Policy) => void;
  onDelete: (p: Policy) => void;
  onToggle: (p: Policy) => void;
  onReload: () => void;
}

function LibraryTab({
  policies,
  stats,
  filters,
  sortBy,
  sortDir,
  page,
  totalPages,
  totalFiltered,
  uniqueSources,
  onFilterChange,
  onSortToggle,
  SortIcon,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onToggle,
  onReload,
}: LibraryTabProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <GatewayStatCard
          icon={<Library className="w-5 h-5" />}
          label="Total"
          value={stats.total ?? 0}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="Active"
          value={stats.active ?? 0}
          colorClass="text-emerald-400"
        />
        <GatewayStatCard
          icon={<ShieldCheck className="w-5 h-5" />}
          label="PERMIT"
          value={stats.permit ?? 0}
          colorClass="text-emerald-400"
        />
        <GatewayStatCard
          icon={<ShieldX className="w-5 h-5" />}
          label="FORBID"
          value={stats.forbid ?? 0}
          colorClass="text-red-400"
        />
      </div>

      <div className="flex items-center gap-3 flex-wrap mt-4 mb-2 py-3 px-4 rounded-xl border border-border bg-card">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search policies..."
            value={filters.search}
            onChange={(e) => onFilterChange("search", e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-card border border-border rounded-lg text-xs text-foreground/80 placeholder-muted-foreground outline-none focus:border-primary/50"
          />
        </div>
        <Select value={filters.effect || "__all__"} onValueChange={(v) => onFilterChange("effect", v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[130px] h-8 text-xs bg-card border-border">
            <SelectValue placeholder="All effects" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="__all__">All effects</SelectItem>
            <SelectItem value="PERMIT">PERMIT</SelectItem>
            <SelectItem value="FORBID">FORBID</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.enabled || "__all__"} onValueChange={(v) => onFilterChange("enabled", v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[130px] h-8 text-xs bg-card border-border">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="true">Enabled</SelectItem>
            <SelectItem value="false">Disabled</SelectItem>
          </SelectContent>
        </Select>
        {uniqueSources.length > 0 && (
          <Select value={filters.source || "__all__"} onValueChange={(v) => onFilterChange("source", v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-[150px] h-8 text-xs bg-card border-border">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="__all__">All sources</SelectItem>
              {uniqueSources.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {policies.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No policies match your filters
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/60">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"14%"}}>
                  <button onClick={() => onSortToggle("name")} className="flex items-center gap-1 hover:text-foreground transition-colors">
                    Policy Name <SortIcon field="name" />
                  </button>
                </th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"22%"}}>Description</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"7%"}}>Effect</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"7%"}}>Source</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"14%"}}>Tags</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"11%"}}>
                  <button onClick={() => onSortToggle("createdAt")} className="flex items-center gap-1 hover:text-foreground transition-colors">
                    Created <SortIcon field="createdAt" />
                  </button>
                </th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"12%"}}>Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{width:"10%"}}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {policies.map((policy) => {
                const tags: string[] = (() => {
                  const raw = policy.tags;
                  if (!raw) return [];
                  if (Array.isArray(raw)) return raw;
                  if (typeof raw === "string") return raw.split(",").map((t: string) => t.trim()).filter(Boolean);
                  return [];
                })();
                return (
                <tr
                  key={policy.id}
                  className={cn(
                    "hover:bg-muted/60 transition-colors",
                    !policy.enabled && "opacity-60"
                  )}
                >
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground text-sm leading-6">{policy.policyName || policy.name}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-muted-foreground line-clamp-2 break-words leading-6" title={policy.description || ""}>
                      {policy.description || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <EffectBadge effect={policy.effect} />
                  </td>
                  <td className="px-3 py-3">
                    {policy.source ? (
                      <span className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border",
                        policy.source === "LLM_GENERATED"
                          ? "bg-primary/10 text-primary border-primary/20"
                          : "bg-muted/60 text-muted-foreground border-border"
                      )}>
                        {policy.source === "LLM_GENERATED" && <Brain className="w-3 h-3" />}
                        {policy.source === "LLM_GENERATED" ? "AI" : policy.source}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary/10 text-primary border border-primary/20"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-muted-foreground">{formatTimestamp(policy.createdAt)}</span>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => onToggle(policy)}
                      className="flex items-center gap-1.5 transition-colors"
                      title={policy.enabled ? "Click to disable" : "Click to enable"}
                    >
                      <div
                        className={cn(
                          "relative w-9 h-[18px] rounded-full transition-colors duration-200",
                          policy.enabled ? "bg-emerald-500" : "bg-muted-foreground/30"
                        )}
                      >
                        <div
                          className={cn(
                            "absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform duration-200",
                            policy.enabled ? "translate-x-[19px]" : "translate-x-[2px]"
                          )}
                        />
                      </div>
                      <span className={cn(
                        "text-xs",
                        policy.enabled ? "text-emerald-400" : "text-muted-foreground"
                      )}>
                        {policy.enabled ? "Active" : "Disabled"}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onView(policy)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEdit(policy)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-500/10 text-muted-foreground hover:text-amber-400 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(policy)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            Showing {Math.min(totalFiltered, (page + 1) * 10)} of {totalFiltered}
          </p>
          <div className="flex items-center gap-1">
            <button
              disabled={page === 0}
              onClick={() => onPageChange(page - 1)}
              className="px-2.5 py-1.5 rounded-md text-xs border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const startPage = Math.max(0, Math.min(page - 2, totalPages - 5));
              const p = startPage + i;
              return (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={cn(
                    "w-8 h-8 rounded-md text-xs border transition-colors",
                    p === page
                      ? "border-primary/50 bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground/80"
                  )}
                >
                  {p + 1}
                </button>
              );
            })}
            <button
              disabled={page >= totalPages - 1}
              onClick={() => onPageChange(page + 1)}
              className="px-2.5 py-1.5 rounded-md text-xs border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

